// Tunable constants — the single source of game-design numbers.
// Faithful to the 1980 arcade where the values are known (scoring, multiplier,
// per-base speed); layout/speeds are chosen to feel right at the logical resolution.

import type { Vec2 } from './state/types';

/** Fixed simulation timestep (seconds). sim == render == 60Hz, no interpolation. */
export const FIXED_DT = 1 / 60;

/** Logical play-field resolution (the shell scales this to the canvas). */
export const FIELD_W = 1024;
export const FIELD_H = 768;
export const GROUND_Y = 704;

export const CITY_COUNT = 6;
export const BASE_COUNT = 3;
export const AMMO_PER_BASE = 10;

/** Base positions (Alpha=left, Bravo=center, Charlie=right). */
export const BASE_POSITIONS: readonly [Vec2, Vec2, Vec2] = [
  { x: 64, y: GROUND_Y },
  { x: 512, y: GROUND_Y },
  { x: 960, y: GROUND_Y },
];

/** City positions: 3 left of Bravo, 3 right of Bravo. */
export const CITY_POSITIONS: readonly [Vec2, Vec2, Vec2, Vec2, Vec2, Vec2] = [
  { x: 160, y: GROUND_Y },
  { x: 256, y: GROUND_Y },
  { x: 352, y: GROUND_Y },
  { x: 672, y: GROUND_Y },
  { x: 768, y: GROUND_Y },
  { x: 864, y: GROUND_Y },
];

/** Global speed scaler. Lower = slower-paced game; higher = faster. */
export const SPEED_SCALE = 0.75;

/** Interceptor speed scaler relative to the original arcade values. */
export const INTERCEPTOR_SPEED_SCALE = 0.5;

/** Interceptor speed (px/s) by base id. Bravo (center) is the fast base. */
export const INTERCEPTOR_SPEED: readonly [number, number, number] = [
  520 * INTERCEPTOR_SPEED_SCALE,
  920 * INTERCEPTOR_SPEED_SCALE,
  520 * INTERCEPTOR_SPEED_SCALE,
];

/** Explosion radius curve. */
export const EXPLOSION_MAX_RADIUS = 56;
export const EXPLOSION_EXPAND_T = 0.35;
export const EXPLOSION_HOLD_T = 0.45;
export const EXPLOSION_CONTRACT_T = 0.5;
export const EXPLOSION_MAX_AGE =
  EXPLOSION_EXPAND_T + EXPLOSION_HOLD_T + EXPLOSION_CONTRACT_T;

/** Enemy descent speed (px/s) at wave 1, and per-wave growth (capped). */
export const ENEMY_BASE_SPEED = 42 * SPEED_SCALE;
export const ENEMY_SPEED_PER_WAVE = 7 * SPEED_SCALE;
export const ENEMY_MAX_SPEED = 150 * SPEED_SCALE;

/** Enemy count per wave: base + growth, capped. */
export const ENEMY_BASE_COUNT = 8;
export const ENEMY_COUNT_PER_WAVE = 2;
export const ENEMY_MAX_COUNT = 26;

// --- scoring (faithful arcade values) ---
export const POINTS_PER_MISSILE = 25;
export const POINTS_PER_SMART_BOMB = 125;
export const POINTS_PER_FLYING = 100;
export const BONUS_PER_UNUSED_MISSILE = 5;
export const BONUS_PER_SURVIVING_CITY = 100;
export const BONUS_CITY_INTERVAL = 10_000;

/** Score multiplier by wave: 1x (W1-2), 2x (W3-4), … 6x (W11+). */
export function multiplierForWave(wave: number): number {
  if (wave <= 2) return 1;
  if (wave <= 4) return 2;
  if (wave <= 6) return 3;
  if (wave <= 8) return 4;
  if (wave <= 10) return 5;
  return 6;
}

/** Phase screen durations (seconds). */
export const WAVE_INTRO_T = 1.6;
export const WAVE_END_T = 2.2;
export const GAME_OVER_T = 3.0;

/** Window over which a wave's spawns are released (seconds). */
export const WAVE_SPAWN_WINDOW = 9;

// --- Iteration 2: MIRV splitting ---
export const MIRV_INTRO_WAVE = 3;
export const MIRV_SPLIT_COUNT = 3;
/** Altitude (y) at which a MIRV warhead splits into children. */
export const MIRV_SPLIT_Y = 280;
export const MIRV_CHANCE_BASE = 0.15;
export const MIRV_CHANCE_PER_WAVE = 0.03;
export const MIRV_CHANCE_MAX = 0.5;

// --- Iteration 2: smart bombs (evasive) ---
export const SMARTBOMB_INTRO_WAVE = 7;
export const SMARTBOMB_CHANCE_BASE = 0.1;
export const SMARTBOMB_CHANCE_PER_WAVE = 0.02;
export const SMARTBOMB_CHANCE_MAX = 0.35;
/** A smart bomb reacts to active blasts within (blastRadius + this) px. */
export const SMARTBOMB_EVADE_RANGE = 90;
export const SMARTBOMB_EVADE_STRENGTH = 0.9;

// --- Iteration 2: flying objects (bomber / satellite) ---
export const FLYING_INTRO_WAVE = 5;
export const FLYING_CHANCE = 0.55; // chance to spawn one flyer in an eligible wave
export const FLYING_SPEED = 75 * SPEED_SCALE;
export const BOMBER_Y = 130;
export const SATELLITE_Y = 72;
export const FLYING_DROP_INTERVAL = 1.5; // seconds between bomb drops
